import {
  Button,
  ButtonGroupToggle,
  Loading,
  Pagination,
  Tag,
} from '@therms/atalaya'
import {
  Alerts,
  ErrorLoadingRetry,
  getNotificationDestination,
  getResolveActionLabel,
  NOTIFICATION_CATEGORY_LABELS,
  NOTIFICATION_PRIORITY_VARIANTS,
  NotificationListItem,
  NotificationsViewModel,
  openNotification,
  RPCCacheStrategy,
  RPCs,
  useRPC,
  useRPCContext,
} from '@therms/react-modules'
import {RPCDocs} from '@therms/rpc-gateway'
import {useEvent, useSingleton} from 'mvc-kit/react'
import {useEffect, useMemo, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {NOTIFICATIONS_ROUTES} from '@src/modules/Notifications/constants'

type FindArgs = RPCDocs.Notifications.FindMyNotifications.v1.Args
type FindData = RPCDocs.Notifications.FindMyNotifications.v1.Data
type NotificationItem =
  RPCDocs.Notifications.FindMyNotifications.v1.NotificationRecipient

const PAGE_SIZE = 50
// The deep link has no fetch-by-id RPC (backend ask pending) — scan up to
// this many rows before showing the "no longer available" notice.
const DEEP_LINK_SCAN_LIMIT = 100
const DEEP_LINK_SCAN_PAGES = 2

type ReadFilter = 'all' | 'unread'

function Notifications() {
  const history = useHistory()
  const {notificationId} = useParams<{notificationId?: string}>()
  const {rpcService} = useRPCContext()
  const vm = useSingleton(NotificationsViewModel)

  const [filter, setFilter] = useState<ReadFilter>('all')
  const [skip, setSkip] = useState(0)
  const [focused, setFocused] = useState<NotificationItem | null>(null)
  const [focusedError, setFocusedError] = useState(false)
  const [focusedGone, setFocusedGone] = useState(false)
  const [scanRetry, setScanRetry] = useState(0)

  const args = useMemo<FindArgs>(
    () => ({
      pagination: {limit: PAGE_SIZE, skip},
      ...(filter === 'unread' ? {read: false} : {}),
    }),
    [filter, skip],
  )

  const {call, data, isCalling, success} = useRPC<FindArgs, FindData>(
    RPCs.Notifications.FindMyNotifications.v1,
    args,
    {cacheStrategy: RPCCacheStrategy.remoteFirst, fire: true},
  )

  // Live updates: refetch on any created/resolved event; clear a resolved
  // focused detail card (contract §5: close open detail views)
  useEvent(vm, 'created', () => call())
  useEvent(vm, 'resolved', ({notificationId: resolvedId}) => {
    call()

    if (focused?.notificationId === resolvedId) {
      setFocused(null)
      setFocusedGone(true)
    }
  })

  // Deep link (/profile/notifications/:id — the backend §8 email fallback):
  // scan up to 2×100 rows for the id; absent = resolved/expired = calm notice.
  // A scan RPC failure is NOT the same as "not found" — retry via scanRetry
  // instead of claiming the notification is gone.
  useEffect(() => {
    if (!notificationId) {
      setFocused(null)
      setFocusedError(false)
      setFocusedGone(false)
      return
    }

    let cancelled = false
    setFocusedError(false)

    ;(async () => {
      for (let page = 0; page < DEEP_LINK_SCAN_PAGES; page += 1) {
        const {data: scanData} =
          await rpcService.Notifications.FindMyNotifications.v1.call({
            pagination: {
              limit: DEEP_LINK_SCAN_LIMIT,
              skip: page * DEEP_LINK_SCAN_LIMIT,
            },
          })

        if (cancelled) return

        const match = scanData.notificationRecipients.find(
          (row) => row.notificationId === notificationId,
        )

        if (match) {
          setFocused(match)
          setFocusedGone(false)

          if (!match.read) {
            vm
              .markRead(match.notificationId)
              .then(() => call().catch(() => {}))
              .catch(() => {})
          }

          return
        }

        if (scanData.notificationRecipients.length < DEEP_LINK_SCAN_LIMIT) {
          break
        }
      }

      if (!cancelled) {
        setFocused(null)
        setFocusedGone(true)
      }
    })().catch(() => {
      if (!cancelled) setFocusedError(true)
    })

    return () => {
      cancelled = true
    }
  }, [notificationId, scanRetry])

  const items = data?.notificationRecipients ?? []
  const total = data?.total ?? 0

  return (
    <div className="space-y-base pb-xl">
      {/* Deep-link detail card */}
      {focused && (
        <div className="border-border bg-surface rounded-lg border p-base">
          <div className="gap-xs flex flex-wrap items-center">
            {(focused.priority === 'high' ||
              focused.priority === 'critical') && (
              <Tag
                value={focused.priority}
                variant={NOTIFICATION_PRIORITY_VARIANTS[focused.priority]}
              />
            )}
            <span className="text-color-strong font-semibold">
              {focused.title}
            </span>
          </div>

          {focused.body && <div className="mt-xs">{focused.body}</div>}

          <div className="text-color-subtle mt-xs text-sm">
            {NOTIFICATION_CATEGORY_LABELS[focused.category]}
          </div>

          <div className="gap-xs mt-sm flex">
            <Button
              onClick={() =>
                window.location.assign(getNotificationDestination(focused))
              }
              size="sm"
              variant="main"
            >
              Open ↗
            </Button>

            {focused.requiresAction && (
              <Button
                onClick={async () => {
                  const result = await vm.resolve(focused.notificationId)
                  if (result) history.push(NOTIFICATIONS_ROUTES.__ROOT__)
                }}
                size="sm"
                variant="positive"
              >
                {getResolveActionLabel(focused.category)}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Deep-link scan failure — a transient network error isn't "gone" */}
      {focusedError && (
        <div className="border-border bg-surface rounded-lg border p-base">
          <ErrorLoadingRetry onRetry={() => setScanRetry((n) => n + 1)} />
        </div>
      )}

      {/* Deep-link gone state — email links routinely outlive resolution */}
      {focusedGone && (
        <div className="border-border bg-surface text-color-subtle rounded-lg border border-dashed p-base">
          <div className="text-color font-semibold">
            This notification is no longer available
          </div>
          <div className="text-sm">
            It may have been handled by a teammate or expired.
          </div>
        </div>
      )}

      <div className="gap-sm flex flex-wrap items-center">
        <div className="text-color-strong grow text-lg font-semibold">
          Notifications
        </div>

        <ButtonGroupToggle<ReadFilter>
          activeKey={filter}
          buttons={[
            {key: 'all', label: 'All'},
            {key: 'unread', label: 'Unread'},
          ]}
          onKeyPress={(key) => {
            setSkip(0)
            setFilter(key)
          }}
          size="sm"
        />

        <Button
          onClick={async () => {
            try {
              await vm.markRead()
              call()
            } catch {
              Alerts.error('There was a problem marking notifications read')
            }
          }}
          size="sm"
          subtle
          variant="main"
        >
          Mark all read
        </Button>
      </div>

      <div className="border-border bg-surface relative overflow-hidden rounded-lg border">
        {isCalling && <Loading overlay />}

        {!isCalling && success === false && (
          <div className="p-xl">
            <ErrorLoadingRetry onRetry={() => call()} />
          </div>
        )}

        {!isCalling && success !== false && !items.length && (
          <div className="text-color-subtle p-xl text-center">
            <div className="text-color-strong font-semibold">
              {filter === 'unread'
                ? "You're all caught up"
                : 'No notifications yet'}
            </div>
            <div className="text-sm">New notifications will show up here.</div>
          </div>
        )}

        {success !== false &&
          items.map((item) => (
            <NotificationListItem
              item={item}
              key={item.id}
              onOpen={openNotification}
              onResolve={() => vm.resolve(item.notificationId)}
            />
          ))}
      </div>

      {total > PAGE_SIZE && (
        <Pagination
          onPageClick={(newSkip) => setSkip(newSkip)}
          recordsPerPage={PAGE_SIZE}
          showDescription
          skippedRecords={skip}
          totalRecords={total}
        />
      )}
    </div>
  )
}

export {Notifications}
